import React from "react";
import { CollapseFAQ, H3, NamedLink } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';

import ContinueIcon from '../../TopbarContainer/Topbar/images/continue-icon.svg';
import USADog from '../images/USA - DOG at home - camylla-battani-gZltlzPun9c-unsplash.jpg'

import css from '../HouseSittingAbroadPage.module.css';
import { usaFaq } from "./faqs";

const USAPage = props => {
    return (
        <div className={css.root}>
            <H3>
                House Sitting in the USA
            </H3>
            <p className={css.info}>
                House sitting in the USA offers a unique opportunity to explore diverse regions of the United States while caring for homes and pets. This mutually beneficial arrangement allows homeowners to leave their property and beloved animals in trusted hands, while house sitters enjoy free accommodation and the chance to live like a local.
            </p>
            <br />
            <p className={css.info}>
                Whether you’re a resident looking for house sitting jobs in the USA or an international visitor exploring this vast country, platforms like HouseSit Match make it easy to connect with verified house and pet owners.
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>

            <div className={css.imageContainerHorizontal}>
                <img className={css.image} src={USADog} alt='Photo by Camylla Battani' />
                <p className={css.info}>
                    Photo by Camylla Battani
                </p>
            </div>

            <p className={css.subtitle}>
                Why Choose HouseSit Match?
            </p>
            <p className={css.info}>
                HouseSit Match is a trusted platform for finding house sitting opportunities and reliable sitters in the USA. With rigorous ID checks, background verification, and a user-friendly interface, we prioritize safety and ease of use for both homeowners and house sitters.
            </p>
            <br />
            <p className={css.info}>
                Our platform facilitates genuine connections through detailed profiles, transparent reviews, and dedicated customer support to ensure a seamless exchange of services. HouseSit Match stands out by combining affordability, security, and a community-focused approach to house sitting arrangements.
            </p>

            <p className={css.subtitle}>
                The Benefits of House Sitting in the USA
            </p>
            <p className={css.info}>
                House sitting in the United States offers numerous advantages for sitters and homeowners:
            </p>

            <ul className={css.list} style={{ marginTop: 0 }}>
                <li>
                    Free accommodation for sitters, reducing travel costs significantly.
                </li>
                <li>
                    Peace of mind for homeowners, knowing their home and pets are cared for.
                </li>
                <li>
                    Opportunities to experience diverse American cultures and lifestyles.
                </li>
                <li>
                    An affordable solution for pet care, eliminating the need for boarding facilities.
                </li>
                <li>
                    A chance to build connections through trusted house sitting networks.
                </li>
            </ul>

            <p className={css.subtitle}>
                Types of House Sitting Arrangements in the USA
            </p>
            <p className={css.info}>
                House sitting arrangements in the USA can vary to suit different needs:
            </p>

            <ul className={css.list}>
                <li>
                    <strong>Pet Sitting: </strong>
                    Caring for dogs, cats, or other pets while homeowners are away.
                </li>
                <li>
                    <strong>Short-Term Sits: </strong>
                    Assignments ranging from a few days to a couple of weeks.
                </li>
                <li>
                    <strong>Long-Term Sits: </strong>
                    Extended stays, often up to several months.
                </li>
                <li>
                    <strong>Luxury House Sits: </strong>
                    Caring for high-end homes, often requiring experienced sitters.
                </li>
                <li>
                    <strong>Rural Retreats: </strong>
                    House sitting opportunities in scenic countryside locations.
                </li>
            </ul>

            <p className={css.subtitle}>
                Popular Locations for House Sitting in the USA
            </p>
            <p className={css.info}>
                Explore these sought-after destinations while house sitting:
            </p>

            <ul className={css.listNumbers}>
                <li>
                    <strong>New York City: </strong>
                    Experience the vibrant energy of the city that never sleeps while caring for homes and pets in one of its many charming neighborhoods.
                </li>
                <li>
                    <strong>Los Angeles, California: </strong>
                    Enjoy sunny weather and proximity to beaches, Hollywood attractions, and cultural hubs in this sprawling metropolis.
                </li>
                <li>
                    <strong>Miami, Florida: </strong>
                    Soak up the tropical vibes with stunning beaches and a lively art scene in this coastal city.
                </li>
                <li>
                    <strong>Austin, Texas: </strong>
                    Known for its live music, thriving food culture, and welcoming atmosphere, Austin offers an exciting yet relaxed house sitting experience.
                </li>
                <li>
                    <strong>Seattle, Washington: </strong>
                    Explore the Pacific Northwest's natural beauty and iconic landmarks like Pike Place Market and the Space Needle.
                </li>
                <li>
                    <strong>Chicago, Illinois: </strong>
                    Discover the Windy City's vibrant neighborhoods, stunning architecture, and deep-dish pizza while house sitting in the Midwest.
                </li>
                <li>
                    <strong>San Francisco, California: </strong>
                    Admire the iconic Golden Gate Bridge, explore historic neighborhoods, and enjoy waterfront views in this tech-savvy city.
                </li>
            </ul>

            <p className={css.subtitle}>
                Costs of House Sitting in the USA
            </p>
            <p className={css.info}>
                House sitting in the USA is typically based on an exchange of services—care for the home and pets in return for free accommodation. This arrangement makes it a cost-effective travel option. Sitters should budget for personal expenses like travel, food, and insurance, while homeowners save significantly on pet care and home maintenance.
            </p>

            <p className={css.subtitle}>
                Legal Considerations
            </p>
            <p className={css.info}>
                When house sitting in the USA, legal compliance is crucial, especially for international sitters:
            </p>
            <ul className={css.list}>
                <li>
                    <strong>Visa Requirements: </strong>
                    Most sitters will need a tourist visa. Ensure your visa aligns with the duration and nature of your stay.
                </li>
                <li>
                    <strong>Tax Implications: </strong>
                    Paid house sitting may involve tax obligations, though many assignments are non-monetary exchanges.
                </li>
                <li>
                    <strong>Insurance: </strong>
                    Both sitters and homeowners should verify their insurance covers house sitting arrangements.
                </li>
            </ul>
            <p className={css.info}>
                For specific visa details, check the U.S. Embassy website in your country of origin.
            </p>

            <p className={css.subtitle}>
                How to Become a House Sitter in the USA
            </p>
            <p className={css.info}>
                Follow these simple steps to start your house sitting journey in the USA:
            </p>
            <ul className={css.list}>
                <li>
                    <strong>Join a Trusted Platform: </strong>
                    Register with HouseSit Match to create a profile.
                </li>
                <li>
                    <strong>Complete Verification: </strong>
                    Submit ID and background checks for approval.
                </li>
                <li>
                    <strong>Craft a Compelling Profile: </strong>
                    Highlight your experience, skills, and references.
                </li>
                <li>
                    <strong>Browse Listings: </strong>
                    Search for house sitting jobs in the USA that match your preferences.
                </li>
                <li>
                    <strong>Apply to Assignments: </strong>
                    Send tailored applications to homeowners.
                </li>
                <li>
                    <strong>Confirm Details: </strong>
                    Agree on responsibilities, dates, and any special requirements.
                </li>
            </ul>
            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>

            <H3>
                FAQs
            </H3>

            <CollapseFAQ
                list={usaFaq}
            />

            <p className={css.subtitle}>
                Testimonials
            </p>
            <p className={css.info}>
                Include real testimonials from sitters who have successfully completed house sitting assignments in the USA.
            </p>

            <p className={css.subtitle}>
                Discover America Through House Sitting
            </p>
            <p className={css.info}>
                House sitting in the USA transforms travel into a rich, immersive experience. It's a chance to explore iconic destinations, connect with local communities, and enjoy cost-effective living arrangements.
            </p>
            <br />
            <p className={css.info}>
                By partnering with HouseSit Match, you'll gain access to a trusted network, ensuring peace of mind for both sitters and homeowners.{" "}
                <NamedLink name="SignupPage">Join our platform</NamedLink>
                {" "}and start your American house sitting adventure today!
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>
        </div>
    )
};

export default USAPage;